/**
 * Trang "Lịch phát thanh" (menu trái) — soạn lịch theo mẫu:
 *   1. Tên lịch, địa bàn (cây có tìm kiếm), thiết bị (mặc định "Chọn tất cả thiết bị").
 *   2. "Thêm ngày phát" → hộp lịch tháng chọn nhiều ngày.
 *   3. Mỗi ngày một thẻ: Sao chép (khung giờ sang ngày khác), Xóa, "Thêm mới khung giờ".
 *   4. Mỗi khung giờ: tên tự sinh, Bắt đầu, Kết thúc = bắt đầu + tổng thời lượng bản tin,
 *      "N bản tin" mở hộp chọn bản tin đã duyệt.
 * Lưu: các khung giờ cùng danh sách bản tin gom thành một lịch (`gomThanhLich`).
 * Giờ nhập là giờ Việt Nam (+07:00).
 */
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { dienVao, vi } from '../ngon-ngu/vi';
import { viLichPhatThanh } from '../ngon-ngu/vi-lich-phat-thanh';
import { viBienTapNoiDung } from '../ngon-ngu/vi-bien-tap';
import { banTinApi, LOAI_BAN_TIN_CUM_LOA, TRANG_THAI_CHON_PHAT, type DongBanTin } from '../api/ban-tin-api';
import { lichPhatApi } from '../api/lich-phat-api';
import { thietBiApi, type DongThietBi, type NutDiaBan } from '../api/thiet-bi-api';
import { dungHoan, dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { dungPhien } from '../trang-thai/ngu-canh-phien';
import { ChonDiaBanCay } from '../thanh-phan/ChonDiaBanCay';
import { HopChonNgay } from '../thanh-phan/HopChonNgay';
import {
  giayTuHms,
  gomThanhLich,
  hienThiNgay,
  hmsTuGiay,
  homNayVn,
  ketThucKhung,
  kiemLoiLich,
  soanTuLich,
  tenKhung,
  tongThoiLuong,
  type KhungGioSoan,
  type NgayPhatSoan,
} from './lich-phat-thanh-tien-ich';
import './lich-phat-thanh.css';

const t = viLichPhatThanh;

export interface LichPhatThanhProps {
  api?: {
    tao: typeof lichPhatApi.tao;
    /** Chế độ sửa (`/lich-phat/:id/sua`); tuỳ chọn để kiểm thử chế độ tạo không phải truyền. */
    layMot?: typeof lichPhatApi.layMot;
    sua?: typeof lichPhatApi.sua;
    danhSachBanTin: typeof banTinApi.danhSach;
    danhSachThietBi: typeof thietBiApi.danhSach;
    cayDiaBan: typeof thietBiApi.cayDiaBan;
  };
}

const API_MAC_DINH: NonNullable<LichPhatThanhProps['api']> = {
  tao: lichPhatApi.tao,
  layMot: lichPhatApi.layMot,
  sua: lichPhatApi.sua,
  danhSachBanTin: banTinApi.danhSach,
  danhSachThietBi: thietBiApi.danhSach,
  cayDiaBan: thietBiApi.cayDiaBan,
};

let demId = 0;
const idMoi = (): string => `k${Date.now().toString(36)}${(demId++).toString(36)}`;

function khungMoi(truoc: KhungGioSoan | undefined, ketThucTruoc: number | null): KhungGioSoan {
  // Khung mới nối tiếp khung cuối; ngày trống thì 05:00:00 như mẫu.
  const batDau = truoc ? Math.min(ketThucTruoc ?? truoc.batDau, 86399) : 5 * 3600;
  return { id: idMoi(), batDau, ketThucTay: null, banTin: [] };
}

function loiThanhChu(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function LichPhatThanh({ api = API_MAC_DINH }: LichPhatThanhProps = {}): ReactNode {
  const phien = dungPhien();
  const dieuHuong = useNavigate();
  const maTinh = phien.hoSo?.maTinh ?? '';
  /** Có `id` trên đường dẫn → sửa lịch đó thay vì tạo mới. */
  const { id: idSua } = useParams();

  const cay = dungTaiDuLieu<NutDiaBan[]>({
    tai: (signal) => api.cayDiaBan({}, signal),
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [],
  });
  const banTin = dungTaiDuLieu<DongBanTin[]>({
    tai: async (signal) =>
      (
        await api.danhSachBanTin(
          { trangThai: TRANG_THAI_CHON_PHAT, loaiBanTin: LOAI_BAN_TIN_CUM_LOA, pageSize: 200 },
          signal,
        )
      ).items,
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [],
  });

  const [diaBan, datDiaBan] = useState(phien.phamViDangChon[0] ?? maTinh);
  const tenDiaBan = useMemo(() => {
    const nut = (cay.duLieu ?? []).find((n) => n.maDinhDanh === diaBan);
    return nut?.ten ?? (diaBan === maTinh ? (phien.hoSo?.tenTinh ?? diaBan) : diaBan);
  }, [cay.duLieu, diaBan, maTinh, phien.hoSo?.tenTinh]);

  const tenMacDinh = `${tenDiaBan} ${hienThiNgay(homNayVn()).replace(/-/g, '/')}`;
  const [tenLich, datTenLich] = useState<string | null>(null);
  const [thietBi, datThietBi] = useState<DongThietBi[]>([]);
  const [dsNgay, datDsNgay] = useState<NgayPhatSoan[]>([]);
  const [hopNgay, datHopNgay] = useState<{ saoChepTu?: string } | null>(null);
  const [hopBanTin, datHopBanTin] = useState<{ ngay: string; khungId: string } | null>(null);
  const [loi, datLoi] = useState<string[]>([]);
  const [thongBao, datThongBao] = useState<string | null>(null);
  const [dangLuu, datDangLuu] = useState(false);
  const [dangTaiLich, datDangTaiLich] = useState(Boolean(idSua));

  // Chế độ sửa: nạp lịch, dựng lại tên, địa bàn, thiết bị và các khung giờ chưa bắt đầu.
  useEffect(() => {
    if (!idSua || !api.layMot) return undefined;
    const huy = new AbortController();
    datDangTaiLich(true);
    api
      .layMot(idSua, huy.signal)
      .then((l) => {
        datTenLich(l.tenLichPhat);
        const coThietBi = l.phamVi.some((p) => (p.CumLoaID ?? []).length > 0);
        if (coThietBi) {
          datDiaBan(l.phamVi.length === 1 ? (l.phamVi[0]?.DichID ?? maTinh) : maTinh);
          datThietBi(
            l.phamVi.flatMap((p) =>
              (p.CumLoaID ?? []).map(
                (ma) =>
                  ({
                    cumLoaId: ma,
                    tenThietBi: ma,
                    nguonId: p.DichID,
                    tenXa: p.TenDich ?? null,
                  }) as DongThietBi,
              ),
            ),
          );
        } else {
          datDiaBan(l.phamVi[0]?.DichID ?? maTinh);
          datThietBi([]);
        }
        datDsNgay(soanTuLich(l, Math.floor(Date.now() / 1000), idMoi));
      })
      .catch((e: unknown) => {
        if (!huy.signal.aborted) datLoi([dienVao(t.loiTaiLich, { loi: loiThanhChu(e) })]);
      })
      .finally(() => {
        if (!huy.signal.aborted) datDangTaiLich(false);
      });
    return () => huy.abort();
    // Chỉ nạp lại khi đổi lịch đang sửa.
  }, [idSua]);

  const thoiLuongTheoMa = useMemo(() => {
    const r: Record<string, number | null> = {};
    for (const b of banTin.duLieu ?? []) r[b.banTinId] = giayTuHms(b.thoiLuong);
    return r;
  }, [banTin.duLieu]);

  const doiNgay = (ngay: string, doi: (n: NgayPhatSoan) => NgayPhatSoan): void =>
    datDsNgay((cu) => cu.map((n) => (n.ngay === ngay ? doi(n) : n)));
  const doiKhung = (ngay: string, id: string, doi: (k: KhungGioSoan) => KhungGioSoan): void =>
    doiNgay(ngay, (n) => ({ ...n, khung: n.khung.map((k) => (k.id === id ? doi(k) : k)) }));

  const themNgay = (moi: string[]): void => {
    datDsNgay((cu) => {
      const co = new Set(cu.map((n) => n.ngay));
      return [...cu, ...moi.filter((x) => !co.has(x)).map((ngay) => ({ ngay, khung: [] }))].sort((a, b) =>
        a.ngay.localeCompare(b.ngay),
      );
    });
  };
  const saoChep = (tu: string, den: string[]): void => {
    datDsNgay((cu) => {
      const goc = cu.find((n) => n.ngay === tu);
      if (!goc) return cu;
      const banSao = (): KhungGioSoan[] =>
        goc.khung.map((k) => ({ ...k, id: idMoi(), banTin: [...k.banTin] }));
      const theoNgay = new Map(cu.map((n) => [n.ngay, n]));
      for (const ngay of den) theoNgay.set(ngay, { ngay, khung: banSao() });
      return [...theoNgay.values()].sort((a, b) => a.ngay.localeCompare(b.ngay));
    });
  };

  const luu = async (): Promise<void> => {
    datThongBao(null);
    const ten = (tenLich ?? tenMacDinh).trim();
    const dsLoi = [
      ...(ten ? [] : [t.canTen]),
      ...(diaBan ? [] : [t.canDiaBan]),
      ...kiemLoiLich(dsNgay, thoiLuongTheoMa, Math.floor(Date.now() / 1000)),
    ];
    datLoi(dsLoi);
    if (dsLoi.length > 0) return;
    const lich = gomThanhLich(ten, dsNgay, phamViLuu(diaBan, tenDiaBan, thietBi), thoiLuongTheoMa);
    datDangLuu(true);
    let daLuu = 0;
    try {
      for (const [i, than] of lich.entries()) {
        // Sửa: nhóm bản tin đầu ghi đè lịch đang sửa, nhóm khác (nếu tách) thành lịch mới.
        if (idSua && api.sua && i === 0) await api.sua(idSua, than);
        else await api.tao(than);
        daLuu++;
      }
      const daLuuChu = idSua ? dienVao(t.daCapNhat, { ten }) : dienVao(t.daLuu, { so: daLuu });
      datThongBao(daLuuChu);
      datDsNgay([]);
      datTenLich(null);
      // Lưu xong thì về trang danh sách lịch; thông báo đi kèm qua trạng thái điều hướng.
      dieuHuong('/lich-phat', { state: { thongBao: daLuuChu } });
    } catch (e) {
      datLoi([
        dienVao(t.loiLuu, { loi: loiThanhChu(e) }),
        ...(daLuu > 0 ? [dienVao(t.daLuuMot, { so: daLuu, tong: lich.length })] : []),
      ]);
    } finally {
      datDangLuu(false);
    }
  };

  const khungDangChon = hopBanTin
    ? dsNgay.find((n) => n.ngay === hopBanTin.ngay)?.khung.find((k) => k.id === hopBanTin.khungId)
    : undefined;

  return (
    <section className="man-hinh lpt" data-man-hinh="lich-phat-thanh">
      <div className="lpt__dau">
        <h1>
          <button
            type="button"
            className="lpt__quay-lai"
            aria-label={t.quayLai}
            onClick={() => dieuHuong('/lich-phat')}
          >
            ←
          </button>
          {idSua ? t.tieuDeSua : t.tieuDe}
        </h1>
        <Link to="/lich-phat" className="lpt__lien-ket">
          {t.xemLichDaLap}
        </Link>
      </div>
      {dangTaiLich ? (
        <p className="lpt__nhan" role="status">
          {t.dangTaiLich}
        </p>
      ) : null}

      <label className="lpt__nhan lpt__nhan--bb" htmlFor="lpt-ten">
        {t.tenLich}
      </label>
      <input
        id="lpt-ten"
        className="lpt__o"
        value={tenLich ?? tenMacDinh}
        maxLength={255}
        onChange={(e) => datTenLich(e.target.value)}
      />

      <div className="lpt__hai-cot">
        <div>
          <span className="lpt__nhan lpt__nhan--bb">{t.diaBan}</span>
          <ChonDiaBanCay
            className="lpt-cay"
            id="lpt-dia-ban"
            nhan={t.diaBan}
            giaTri={diaBan}
            cay={cay.duLieu ?? []}
            nhanTatCa={t.diaBanTatCa}
            onDoi={(v) => {
              datDiaBan(v || maTinh);
              datThietBi([]);
            }}
          />
        </div>
        <ChonThietBi
          diaBan={diaBan}
          daChon={thietBi}
          onDoi={datThietBi}
          danhSachThietBi={api.danhSachThietBi}
        />
      </div>

      <div className="lpt__nhan">
        {dienVao(t.dsNgay, { so: dsNgay.length })} <span aria-hidden="true">🗓</span>)
      </div>
      <div className="lpt__vung-ngay" data-vung="danh-sach-ngay">
        {dsNgay.map((n) => (
          <article key={n.ngay} className="lpt-ngay" data-ngay={n.ngay}>
            <div className="lpt-ngay__dau">
              <strong>{hienThiNgay(n.ngay)}</strong>
              <span className="lpt-ngay__nut">
                <button type="button" className="lpt-lk" onClick={() => datHopNgay({ saoChepTu: n.ngay })}>
                  ⧉ {t.saoChep}
                </button>
                <button
                  type="button"
                  className="lpt-lk lpt-lk--do"
                  onClick={() => datDsNgay((cu) => cu.filter((x) => x.ngay !== n.ngay))}
                >
                  🗑 {t.xoa}
                </button>
              </span>
            </div>
            {n.khung.length > 0 ? (
              <div className="lpt-khung lpt-khung--dau" aria-hidden="true">
                <span>{t.cot.ten}</span>
                <span>{t.cot.batDau}</span>
                <span>{t.cot.ketThuc}</span>
                <span>{t.cot.noiDung}</span>
                <span />
              </div>
            ) : null}
            {n.khung.map((k) => {
              const tong = tongThoiLuong(k.banTin, thoiLuongTheoMa);
              const kt = ketThucKhung(k, thoiLuongTheoMa);
              const nhapTay = k.banTin.length > 0 && tong === null;
              return (
                <div key={k.id} className="lpt-khung" data-khung={k.id}>
                  <input className="lpt__o" value={tenKhung(k.batDau, kt)} disabled aria-label={t.cot.ten} />
                  <OGio
                    nhan={t.cot.batDau}
                    giay={k.batDau}
                    onDoi={(g) => doiKhung(n.ngay, k.id, (x) => ({ ...x, batDau: g }))}
                  />
                  <OGio
                    nhan={t.cot.ketThuc}
                    giay={kt ?? k.batDau}
                    tat={!nhapTay}
                    goiY={nhapTay ? t.ketThucTay : undefined}
                    onDoi={(g) => doiKhung(n.ngay, k.id, (x) => ({ ...x, ketThucTay: g }))}
                  />
                  <button
                    type="button"
                    className="lpt-khung__noi-dung"
                    data-hanh-dong="chon-ban-tin"
                    onClick={() => datHopBanTin({ ngay: n.ngay, khungId: k.id })}
                  >
                    {dienVao(t.soBanTin, { so: k.banTin.length })}
                  </button>
                  <button
                    type="button"
                    className="lpt-khung__xoa"
                    aria-label={t.xoaKhung}
                    onClick={() =>
                      doiNgay(n.ngay, (x) => ({ ...x, khung: x.khung.filter((y) => y.id !== k.id) }))
                    }
                  >
                    —
                  </button>
                </div>
              );
            })}
            <button
              type="button"
              className="lpt-them-khung"
              data-hanh-dong="them-khung"
              onClick={() =>
                doiNgay(n.ngay, (x) => {
                  const cuoi = x.khung.at(-1);
                  return {
                    ...x,
                    khung: [...x.khung, khungMoi(cuoi, cuoi ? ketThucKhung(cuoi, thoiLuongTheoMa) : null)],
                  };
                })
              }
            >
              + {t.themKhung}
            </button>
          </article>
        ))}
        <button
          type="button"
          className="nut nut--chinh lpt__them-ngay"
          data-hanh-dong="them-ngay"
          onClick={() => datHopNgay({})}
        >
          + {t.themNgay}
        </button>
      </div>

      {loi.length > 0 ? (
        <ul className="lpt__loi" role="alert">
          {loi.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      ) : null}
      {thongBao ? (
        <p className="lpt__thong-bao" role="status">
          {thongBao} <Link to="/lich-phat">{t.xemLichDaLap}</Link>
        </p>
      ) : null}

      <div className="lpt__chan">
        <button type="button" className="nut" onClick={() => dieuHuong('/lich-phat')}>
          {t.huy}
        </button>
        <button
          type="button"
          className="nut nut--chinh"
          data-hanh-dong="luu-lich"
          disabled={dangLuu || dangTaiLich}
          onClick={() => void luu()}
        >
          {dangLuu ? t.dangLuu : t.luu}
        </button>
      </div>

      {hopNgay ? (
        <HopChonNgay
          tieuDe={
            hopNgay.saoChepTu
              ? dienVao(t.lich.tieuDeSaoChep, { ngay: hienThiNgay(hopNgay.saoChepTu) })
              : undefined
          }
          coDinh={hopNgay.saoChepTu ? [hopNgay.saoChepTu] : dsNgay.map((n) => n.ngay)}
          onHuy={() => datHopNgay(null)}
          onXacNhan={(ds) => {
            if (hopNgay.saoChepTu) saoChep(hopNgay.saoChepTu, ds);
            else themNgay(ds);
            datHopNgay(null);
          }}
        />
      ) : null}

      {hopBanTin && khungDangChon ? (
        <HopChonBanTin
          tenKhung={`${hienThiNgay(hopBanTin.ngay)} ${tenKhung(khungDangChon.batDau, ketThucKhung(khungDangChon, thoiLuongTheoMa))}`}
          dsBanTin={banTin.duLieu}
          dangTai={banTin.trangThai === 'dang-tai'}
          daChon={khungDangChon.banTin}
          thoiLuongTheoMa={thoiLuongTheoMa}
          onHuy={() => datHopBanTin(null)}
          onXacNhan={(ds) => {
            doiKhung(hopBanTin.ngay, hopBanTin.khungId, (x) => ({ ...x, banTin: ds, ketThucTay: null }));
            datHopBanTin(null);
          }}
        />
      ) : null}
    </section>
  );
}

/**
 * Phạm vi lưu cho lịch: chọn thiết bị cụ thể → gom theo XÃ của thiết bị (DichID = mã xã,
 * vd H44.167, CumLoaID = mã thiết bị) để danh sách lịch và gói DV002 hiện đúng địa bàn
 * nhận; không chọn → địa bàn đang chọn với `CumLoaID: []` (toàn bộ thiết bị).
 */
export function phamViLuu(
  diaBan: string,
  tenDiaBan: string,
  thietBi: Pick<DongThietBi, 'cumLoaId' | 'nguonId' | 'tenXa'>[],
): { DichID: string; TenDich: string; CumLoaID: string[] }[] {
  if (thietBi.length === 0) return [{ DichID: diaBan, TenDich: tenDiaBan, CumLoaID: [] }];
  const theoXa = new Map<string, { DichID: string; TenDich: string; CumLoaID: string[] }>();
  for (const d of thietBi) {
    const ma = d.nguonId || diaBan;
    const muc = theoXa.get(ma) ?? { DichID: ma, TenDich: d.tenXa ?? '', CumLoaID: [] };
    muc.CumLoaID.push(d.cumLoaId);
    theoXa.set(ma, muc);
  }
  return [...theoXa.values()];
}

/* ---------------------------------- Ô giờ ---------------------------------- */

/**
 * Ô nhập giờ `HH:mm:ss` 24 giờ, áp dụng khi rời ô (gõ dở không làm nhảy giá trị). Trang soạn lịch
 * bảng tin dùng chung.
 */
export function OGio({
  nhan,
  giay,
  onDoi,
  tat = false,
  goiY,
}: {
  nhan: string;
  giay: number;
  onDoi: (giay: number) => void;
  tat?: boolean;
  goiY?: string;
}): ReactNode {
  const [chu, datChu] = useState(hmsTuGiay(giay));
  useEffect(() => datChu(hmsTuGiay(giay)), [giay]);
  const apDung = (): void => {
    const g = giayTuHms(chu);
    if (g === null) datChu(hmsTuGiay(giay));
    else if (g !== giay) onDoi(g);
    else datChu(hmsTuGiay(g));
  };
  return (
    <span className="lpt-gio">
      <input
        className="lpt__o"
        aria-label={nhan}
        inputMode="numeric"
        placeholder="HH:mm:ss"
        title={goiY}
        disabled={tat}
        value={chu}
        onChange={(e) => datChu(e.target.value)}
        onBlur={apDung}
        onKeyDown={(e) => {
          if (e.key === 'Enter') apDung();
        }}
      />
      <span className="lpt-gio__icon" aria-hidden="true">
        🕓
      </span>
    </span>
  );
}

/* ------------------------------- Chọn thiết bị ------------------------------- */

const SO_THIET_BI_HIEN = 50;

function ChonThietBi({
  diaBan,
  daChon,
  onDoi,
  danhSachThietBi,
}: {
  diaBan: string;
  daChon: DongThietBi[];
  onDoi: (ds: DongThietBi[]) => void;
  danhSachThietBi: typeof thietBiApi.danhSach;
}): ReactNode {
  const [mo, datMo] = useState(false);
  const [tim, datTim] = useState('');
  const timHoan = dungHoan(tim, 300);
  const goc = useRef<HTMLDivElement | null>(null);
  const idDs = useId();

  const tong = dungTaiDuLieu<number>({
    tai: async (signal) =>
      (
        await danhSachThietBi(
          { loaiThietBi: 'cum_thu_phat_thanh', nguonId: diaBan || undefined, pageSize: 1 },
          signal,
        )
      ).total,
    laRong: () => false,
    dangLoc: false,
    phuThuoc: [diaBan],
  });
  const ds = dungTaiDuLieu<DongThietBi[]>({
    tai: async (signal) =>
      (
        await danhSachThietBi(
          {
            loaiThietBi: 'cum_thu_phat_thanh',
            nguonId: diaBan || undefined,
            keyword: timHoan || undefined,
            pageSize: SO_THIET_BI_HIEN,
            sapXep: 'tenThietBi',
          },
          signal,
        )
      ).items,
    laRong: (d) => d.length === 0,
    dangLoc: Boolean(timHoan),
    phuThuoc: [diaBan, timHoan, mo],
    tat: !mo,
  });

  useEffect(() => {
    if (!mo) return;
    const dong = (e: MouseEvent): void => {
      if (goc.current && !goc.current.contains(e.target as Node)) datMo(false);
    };
    document.addEventListener('mousedown', dong);
    return () => document.removeEventListener('mousedown', dong);
  }, [mo]);

  const tapChon = new Set(daChon.map((d) => d.cumLoaId));
  const bat = (d: DongThietBi): void =>
    onDoi(tapChon.has(d.cumLoaId) ? daChon.filter((x) => x.cumLoaId !== d.cumLoaId) : [...daChon, d]);

  return (
    <div className="lpt-tb" ref={goc}>
      <span className="lpt__nhan">{dienVao(t.thietBi, { so: tong.duLieu ?? 0 })}</span>
      <div
        className="lpt-tb__o"
        role="combobox"
        aria-expanded={mo}
        aria-controls={idDs}
        tabIndex={0}
        onClick={() => datMo(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === 'ArrowDown') datMo(true);
          if (e.key === 'Escape') datMo(false);
        }}
      >
        {daChon.length === 0 ? (
          <span className="lpt-tb__the">{t.tatCaThietBi}</span>
        ) : (
          daChon.map((d) => (
            <span key={d.cumLoaId} className="lpt-tb__the">
              {d.tenThietBi || d.cumLoaId}
              <button
                type="button"
                aria-label={`${t.boChon} ${d.tenThietBi ?? d.cumLoaId}`}
                onClick={(e) => {
                  e.stopPropagation();
                  bat(d);
                }}
              >
                ×
              </button>
            </span>
          ))
        )}
        <span className="lpt-tb__mui" aria-hidden="true">
          ⌄
        </span>
      </div>
      {mo ? (
        <div className="lpt-tb__ds" id={idDs}>
          <input
            className="lpt__o"
            autoFocus
            placeholder={t.timThietBi}
            value={tim}
            onChange={(e) => datTim(e.target.value)}
          />
          <ul role="listbox" aria-multiselectable="true">
            <li role="option" aria-selected={daChon.length === 0}>
              <label>
                <input type="checkbox" checked={daChon.length === 0} onChange={() => onDoi([])} />
                {t.tatCaThietBi}
              </label>
            </li>
            {ds.trangThai === 'dang-tai' ? <li className="lpt-tb__phu">{t.dangTaiThietBi}</li> : null}
            {ds.trangThai === 'rong' || ds.trangThai === 'rong-loc' ? (
              <li className="lpt-tb__phu">{t.khongCoThietBi}</li>
            ) : null}
            {(ds.duLieu ?? []).map((d) => (
              <li key={d.cumLoaId} role="option" aria-selected={tapChon.has(d.cumLoaId)}>
                <label>
                  <input type="checkbox" checked={tapChon.has(d.cumLoaId)} onChange={() => bat(d)} />
                  {d.tenThietBi || d.cumLoaId} <small>{d.cumLoaId}</small>
                </label>
              </li>
            ))}
            {(ds.duLieu ?? []).length >= SO_THIET_BI_HIEN ? (
              <li className="lpt-tb__phu">{dienVao(t.hienThiToiDa, { so: SO_THIET_BI_HIEN })}</li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------- Chọn bản tin -------------------------------- */

function HopChonBanTin({
  tenKhung: ten,
  dsBanTin,
  dangTai,
  daChon,
  thoiLuongTheoMa,
  onHuy,
  onXacNhan,
}: {
  tenKhung: string;
  dsBanTin: DongBanTin[] | null;
  dangTai: boolean;
  daChon: string[];
  thoiLuongTheoMa: Record<string, number | null>;
  onHuy: () => void;
  onXacNhan: (ds: string[]) => void;
}): ReactNode {
  const c = t.chonBanTin;
  const idTieuDe = useId();
  const [chon, datChon] = useState<string[]>(daChon);
  const [tim, datTim] = useState('');
  const loc = (dsBanTin ?? []).filter(
    (b) => !tim.trim() || b.tieuDe.toLowerCase().includes(tim.trim().toLowerCase()),
  );
  const tong = tongThoiLuong(chon, thoiLuongTheoMa);
  const bat = (ma: string): void =>
    datChon((cu) => (cu.includes(ma) ? cu.filter((x) => x !== ma) : [...cu, ma]));

  return (
    <div className="man-che" role="presentation">
      <div className="hop-thoai lpt-bt" role="dialog" aria-modal="true" aria-labelledby={idTieuDe}>
        <div className="lpt-hop__dau">
          <h2 id={idTieuDe}>{dienVao(c.tieuDe, { khung: ten })}</h2>
          <button type="button" className="lpt-hop__dong" aria-label={t.lich.dong} onClick={onHuy}>
            ×
          </button>
        </div>
        <input className="lpt__o" placeholder={c.tim} value={tim} onChange={(e) => datTim(e.target.value)} />
        <div className="lpt-bt__bang">
          <table className="bang">
            <thead>
              <tr>
                <th className="bang__giua">{c.cot.chon}</th>
                <th className="bang__giua">{c.cot.thuTu}</th>
                <th>{c.cot.tieuDe}</th>
                <th className="bang__giua">{c.cot.thoiLuong}</th>
                <th>{c.cot.linhVuc}</th>
                <th>{c.cot.uuTien}</th>
              </tr>
            </thead>
            <tbody>
              {dangTai ? (
                <tr>
                  <td colSpan={6}>{c.dangTai}</td>
                </tr>
              ) : loc.length === 0 ? (
                <tr>
                  <td colSpan={6}>{c.rong}</td>
                </tr>
              ) : (
                loc.map((b) => {
                  const viTri = chon.indexOf(b.banTinId);
                  return (
                    <tr key={b.id} data-ban-tin={b.banTinId}>
                      <td className="bang__giua">
                        <input
                          type="checkbox"
                          aria-label={b.tieuDe}
                          checked={viTri >= 0}
                          onChange={() => bat(b.banTinId)}
                        />
                      </td>
                      <td className="bang__giua">{viTri >= 0 ? viTri + 1 : ''}</td>
                      <td>{b.tieuDe}</td>
                      <td className="bang__giua">{b.thoiLuong ?? c.chuaRo}</td>
                      <td>
                        {vi.banTin.linhVucNhan[b.loaiLinhVuc as keyof typeof vi.banTin.linhVucNhan] ??
                          b.loaiLinhVuc}
                      </td>
                      <td>
                        {viBienTapNoiDung.uuTienNhan[
                          b.mucDoUuTien as keyof typeof viBienTapNoiDung.uuTienNhan
                        ] ?? b.mucDoUuTien}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <p className="lpt-bt__tong">
          {dienVao(c.tong, { so: chon.length, tl: tong === null ? c.chuaRo : hmsTuGiay(tong) })}
        </p>
        <div className="lpt-hop__nut">
          <button type="button" className="nut lpt-nut-huy" onClick={onHuy}>
            {c.huy}
          </button>
          <button
            type="button"
            className="nut nut--chinh"
            data-hanh-dong="xac-nhan-ban-tin"
            onClick={() => onXacNhan(chon)}
          >
            {c.xacNhan}
          </button>
        </div>
      </div>
    </div>
  );
}
